import { useId, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SourceDetails } from "../source-result-row";
import { stateDisplayName } from "../state-names";
import type { ReportItem, RunScope } from "../types";
import {
  confirmedAtLabel,
  NO_RESULT_PLACEHOLDER,
  publishedAtLabel,
  recordCountLabel,
  stateCheckView,
  type SourceCheck,
  type StateRow,
} from "./state-rows";

const COLUMN_COUNT = 5;
const HEADER_CELL = "bg-slate-50 px-2 py-1.5 font-medium";

function StateTableRow({
  row,
  check,
  runId,
  scope,
  issues,
}: {
  row: StateRow;
  check: SourceCheck;
  runId: string;
  scope: RunScope;
  issues?: ReportItem["issues"];
}) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const { source, item } = row;
  const name = stateDisplayName(source.stateCode);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <>
      <tr>
        <td className="px-2 py-1">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={detailsId}
            aria-label={`${open ? "Hide" : "View"} details for ${name}`}
            onClick={() => setOpen(!open)}
            className="flex cursor-pointer items-center gap-1 rounded-sm font-medium text-foreground hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Chevron className="size-3.5 shrink-0" aria-hidden />
            {name}
          </button>
        </td>
        <td className="px-2 py-1 text-muted-foreground">
          {publishedAtLabel(source)}
        </td>
        <td className="px-2 py-1 text-muted-foreground">
          {confirmedAtLabel(source)}
        </td>
        <td className="px-2 py-1 text-muted-foreground">
          {recordCountLabel(source)}
        </td>
        <td className="px-2 py-1">{item?.label ?? NO_RESULT_PLACEHOLDER}</td>
      </tr>
      {open && (
        <tr id={detailsId}>
          <td colSpan={COLUMN_COUNT} className="bg-slate-50 px-2 py-3">
            <SourceDetails
              check={stateCheckView({ check, source, item })}
              runId={runId}
              scope={scope}
              resultKey={source.resultKey}
              issues={issues}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Every state's list facts and result behind one disclosure. A state's name
 * opens its full evidence and possible matches in place. */
export function AllStatesTable({
  rows,
  check,
  runId,
  scope,
  issues,
}: {
  rows: StateRow[];
  check: SourceCheck;
  runId: string;
  scope: RunScope;
  issues?: ReportItem["issues"];
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="space-y-2 p-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className="flex cursor-pointer items-center gap-1 rounded-sm text-xs font-medium text-foreground hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Chevron className="size-3.5 shrink-0" aria-hidden />
        {open ? "Hide" : "Show"} state lists and per-state details
      </button>
      {open && (
        <div id={panelId} className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className={HEADER_CELL}>
                  State
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Data published
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Confirmed current
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Records
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Result
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row) => (
                <StateTableRow
                  key={row.source.resultKey}
                  row={row}
                  check={check}
                  runId={runId}
                  scope={scope}
                  issues={issues}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
